import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { styled } from '@compiled/react';
import { type LoomVideo } from '@loomhq/record-sdk';
import { IconButton } from '@atlaskit/button/new';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/loading-button';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { withFlags, type FlagsStateContainer } from '@confluence/flags';
import type { OpenLoomRecorderParams } from '@confluence/loom-utils';
import { useLoomEntryPointVariant, useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import { PageHeaderLoomButtonSpotlight } from '@confluence/loom-onboarding';
import { useSkippableCoordination } from '@confluence/skippable-coordination-client';
import { useEditPageLoadingActions } from '@confluence/load-edit-page/entry-points/EditPageLoadingContext';
import { getUrlForContentType } from '@confluence/content-types-utils';
import {
	ExperienceTrackerContext,
	LOOM_PAGE_HEADER_CONTENT_INSERTION,
	ExperienceTimeout,
} from '@confluence/experience-tracker';
import { type useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import { PageHeaderLoomButtonQuery } from './PageHeaderLoomButtonQuery.graphql';
import type {
	PageHeaderLoomButtonQuery as PageHeaderLoomButtonQueryType,
	PageHeaderLoomButtonQueryVariables,
} from './__types__/PageHeaderLoomButtonQuery';
import { useLoomInsertUrlState } from './useLoomInsertUrlState';
import { LoomVideoIcon } from './VideoIcon';

type PageHeaderLoomButtonComponentProps = {
	contentId: string;
	dataVC?: string;
	label: string;
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	ssrPlaceholderIdProp?: ReturnType<typeof useSSRPlaceholderReplaceIdProp>;
	isCircle?: boolean;
	testId?: string;
};

export enum PostLoomRecordingCTA {
	EDITOR_INSERT = 'editorInsert',
	PAGE_COMMENT_INSERT = 'pageCommentInsert',
	COPY_LINK = 'copyLink',
	OPEN_ON_LOOM = 'openOnLoom',
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomButtonComponent: FC<PageHeaderLoomButtonComponentProps> = ({
	contentId,
	dataVC,
	label,
	spaceKey,
	contentType,
	flags,
	ssrPlaceholderIdProp,
	isCircle = false,
	testId,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { loadEditor } = useEditPageLoadingActions();
	const [, loomInsertUrlActions] = useLoomInsertUrlState();

	const saveLoomUrlAndLoadEditor = (video: LoomVideo) => {
		loomInsertUrlActions.updateFullPageEditorLoomUrl(video);
		const redirectUrl = getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		});
		loadEditor({
			contentId,
			spaceKey,
			redirectUrl,
			contentType,
		});
	};

	const beginExperienceTracker = (postLoomRecordingCTA: PostLoomRecordingCTA) => {
		experienceTracker.start({
			name: LOOM_PAGE_HEADER_CONTENT_INSERTION,
			timeout: ExperienceTimeout.LOOM_PAGE_HEADER_CONTENT_INSERTION,
			attributes: { postLoomRecordingCTA },
		});
	};

	const { entryPointVariant } = useLoomEntryPointVariant();

	const { data } = useQuery<PageHeaderLoomButtonQueryType, PageHeaderLoomButtonQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		PageHeaderLoomButtonQuery,
		{
			variables: { contentId },
		},
	);

	const isContentContributor = Boolean(data?.contentContributors?.isCurrentUserContributor);

	let openLoomRecorderParams: OpenLoomRecorderParams | undefined;
	let postLoomRecordingCTA: PostLoomRecordingCTA = PostLoomRecordingCTA.OPEN_ON_LOOM;
	let resetInsertFunctionOnUnmount = false;

	const contentData = data?.content?.nodes?.[0];

	const canEdit = contentData?.operations?.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);
	const canComment = contentData?.operations?.some(
		(operation) => operation?.targetType === 'comment' && operation?.operation === 'create',
	);

	if (canEdit && isContentContributor) {
		// editor mode insert
		postLoomRecordingCTA = PostLoomRecordingCTA.EDITOR_INSERT;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		};
	} else if (canComment) {
		// page comment insert
		postLoomRecordingCTA = PostLoomRecordingCTA.PAGE_COMMENT_INSERT;
		// Set to true so that CTA 'Add to comment' will not render if user has navigated to
		// different page post Loom recording.
		resetInsertFunctionOnUnmount = true;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				loomInsertUrlActions.updatePageCommentEditorLoomUrl(video);
				const openPageCommentEditorButton = document.getElementById('addCommentButton');
				openPageCommentEditorButton && openPageCommentEditorButton.click();
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Add to comment',
		};
	} else {
		postLoomRecordingCTA = PostLoomRecordingCTA.COPY_LINK;
		// Not defining openLoomRecorderParams here so that
		// undefined will be passed to the Loom SDK resulting
		// in the Loom SDK rendering a single CTA of 'Copy link'
	}

	const { openLoomRecorder, isOpeningRecorder, isLoomRecorderInitialized, hasRecordingAccess } =
		useLoomRecorderEntryPoint({
			entryPointLocation: 'pageHeader',
			flags,
			resetInsertFunctionOnUnmount,
		});

	const shouldActivateLoomGAOnboarding =
		entryPointVariant === 'CO_USE' && hasRecordingAccess && isLoomRecorderInitialized;

	const [isLoomGAOnboardingActive, stopLoomGAOnboarding] = useSkippableCoordination(
		'cc-loom-onboarding-spotlight-editor',
		!(shouldActivateLoomGAOnboarding && fg('confluence_m25_onboarding_spotlights')),
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createLoomHeaderItem',
				source: 'pageHeader',
				attributes: {
					postLoomRecordingCTA,
				},
			},
		}).fire();

		void openLoomRecorder(openLoomRecorderParams);
	}, [createAnalyticsEvent, openLoomRecorder, openLoomRecorderParams, postLoomRecordingCTA]);

	const handleSpotlightClose = useCallback(() => {
		if (isLoomGAOnboardingActive) {
			void stopLoomGAOnboarding();
		}
	}, [stopLoomGAOnboarding, isLoomGAOnboardingActive]);

	const LoomButtonWithTooltip = () => {
		return (
			<Tooltip content={label}>
				{(tooltipProps) => (
					<ButtonSpacer>
						{isCircle ? (
							<IconButton
								label={label}
								{...tooltipProps}
								isLoading={isOpeningRecorder}
								onClick={handleClick}
								appearance="subtle"
								shape="circle"
								icon={LoomVideoIcon}
								testId={testId}
							/>
						) : (
							<Button
								appearance="subtle"
								iconAfter={<LoomVideoIcon label="" color={token('color.text')} />}
								aria-label={label}
								{...tooltipProps}
								onClick={handleClick}
								isLoading={isOpeningRecorder}
								data-vc={dataVC}
								{...ssrPlaceholderIdProp}
								testId={testId}
							/>
						)}
					</ButtonSpacer>
				)}
			</Tooltip>
		);
	};

	if (
		isLoomGAOnboardingActive &&
		entryPointVariant === 'CO_USE' &&
		fg('confluence_m25_onboarding_spotlights')
	) {
		return (
			<PageHeaderLoomButtonSpotlight onTryClick={handleClick} onClose={handleSpotlightClose}>
				<LoomButtonWithTooltip />
			</PageHeaderLoomButtonSpotlight>
		);
	}

	return <LoomButtonWithTooltip />;
};

export const PageHeaderLoomButton = withFlags(PageHeaderLoomButtonComponent);
